// src/screens/ProfileBadgeScreen.js
// ============================================================================
// PROFILE BADGE — account selector for the earned Founder badge (2026-07-07).
//
// Live preview of the orbiting-die Founder aura + options to display it or
// hide it. The Founder badge is gated to the first 1,000 members (yearly any
// tier, or monthly Pro/Elite); admins may choose it too. Backend callables:
// getMyBadges, setProfileBadge.
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  View, Text, ScrollView, TouchableOpacity, ActivityIndicator, StyleSheet,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import Header from '../components/Header';
import NavBar from '../components/NavBar';
import FounderBadge from '../components/FounderBadge';
import { useError } from '../components/ErrorContext';

function initials(name) {
  return String(name || 'You').replace(/[^A-Za-z0-9]/g, '').slice(0, 2).toUpperCase() || 'YOU';
}

export default function ProfileBadgeScreen({ navigation }) {
  const { showError } = useError();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const me = getAuth().currentUser;

  const load = useCallback(async () => {
    try {
      const res = await httpsCallable(getFunctions(), 'getMyBadges')({});
      setData(res.data);
    } catch (err) {
      console.error('[ProfileBadge] load failed:', err);
      showError('Could not load badges', err?.message || 'Please try again.');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => { load(); }, [load]);

  const choose = async (badge) => {
    if (busy || data?.current === badge) return;
    setBusy(true);
    try {
      await httpsCallable(getFunctions(), 'setProfileBadge')({ badge });
      setData((d) => ({ ...d, current: badge }));
    } catch (err) {
      showError('Could not update badge', err?.message || 'Please try again.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <View style={styles.screen}>
        <Header navigation={navigation} />
        <View style={styles.center}><ActivityIndicator size="large" color="#FCD000" /></View>
        <NavBar navigation={navigation} />
      </View>
    );
  }

  const current = data?.current || 'none';
  const eligible = !!data?.eligibleFounder;
  const previewInits = initials(me?.displayName);

  return (
    <View style={styles.screen}>
      <Header navigation={navigation} />
      <ScrollView style={{ flex: 1 }} contentContainerStyle={styles.scroll} showsVerticalScrollIndicator={false}>
        <TouchableOpacity
          style={styles.backLink}
          onPress={() => (navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Profile'))}
        >
          <Ionicons name="chevron-back" size={18} color="#FCD000" />
          <Text style={styles.backLinkTxt}>Back</Text>
        </TouchableOpacity>
        <Text style={styles.pageTitle}>Profile Badge</Text>
        <Text style={styles.pageSub}>Choose the badge that appears on your public profile.</Text>

        {/* Live preview */}
        <View style={styles.previewCard}>
          <FounderBadge size={82} active={current === 'founder'}>
            <View style={styles.previewAvatar}>
              <Text style={styles.previewAvatarTxt}>{previewInits}</Text>
            </View>
          </FounderBadge>
          <Text style={styles.previewLabel}>
            {current === 'founder'
              ? (data?.founderNumber ? `Founder #${data.founderNumber}` : 'Founder')
              : 'No badge'}
          </Text>
        </View>

        {/* Options */}
        <TouchableOpacity
          style={[styles.option, current === 'none' && styles.optionOn]}
          onPress={() => choose('none')}
          disabled={busy}
        >
          <View style={{ flex: 1 }}>
            <Text style={styles.optionTitle}>No badge</Text>
            <Text style={styles.optionSub}>A clean profile with no aura.</Text>
          </View>
          {current === 'none' && <Ionicons name="checkmark-circle" size={22} color="#FCD000" />}
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.option, current === 'founder' && styles.optionOn, !eligible && styles.optionLocked]}
          onPress={() => (eligible ? choose('founder') : null)}
          disabled={busy || !eligible}
          activeOpacity={eligible ? 0.7 : 1}
        >
          <View style={{ flex: 1 }}>
            <View style={styles.optionTitleRow}>
              <Text style={styles.optionTitle}>Founder badge</Text>
              {!eligible && <Ionicons name="lock-closed" size={13} color="#888" style={{ marginLeft: 6 }} />}
            </View>
            <Text style={styles.optionSub}>
              The gold King Crit die orbits your profile with an electric-blue glow.
              {data?.founderNumber ? `  You're Founder #${data.founderNumber}.` : ''}
            </Text>
            {!eligible && (
              <Text style={styles.lockHint}>
                Earn it as one of the first {data?.cap || 1000} on a qualifying plan: any paid plan
                billed yearly, or Pro or Elite billed monthly. {data?.remaining} of {data?.cap} left.
              </Text>
            )}
          </View>
          {current === 'founder'
            ? <Ionicons name="checkmark-circle" size={22} color="#FCD000" />
            : eligible ? <Ionicons name="chevron-forward" size={20} color="#666" /> : null}
        </TouchableOpacity>

        {!eligible && (
          <TouchableOpacity style={styles.cta} onPress={() => navigation.navigate('Subscription')}>
            <Text style={styles.ctaTxt}>View plans</Text>
          </TouchableOpacity>
        )}
        {data?.isAdmin && (
          <Text style={styles.adminNote}>Admin: the Founder badge is available to you for preview.</Text>
        )}

        <View style={{ height: 40 }} />
      </ScrollView>
      <NavBar navigation={navigation} />
    </View>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: '#0b0b0b' },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  scroll: { padding: 16, maxWidth: 620, width: '100%', alignSelf: 'center' },
  backLink: { flexDirection: 'row', alignItems: 'center', marginBottom: 8 },
  backLinkTxt: { color: '#FCD000', fontWeight: '700', fontSize: 14 },
  pageTitle: { color: '#fff', fontSize: 22, fontWeight: '800' },
  pageSub: { color: '#aaa', fontSize: 13, marginTop: 4, marginBottom: 16 },

  previewCard: {
    backgroundColor: '#101010', borderWidth: 1, borderColor: '#242424', borderRadius: 14,
    padding: 20, alignItems: 'center', marginBottom: 16, minHeight: 210, justifyContent: 'center',
  },
  previewAvatar: { width: 82, height: 82, borderRadius: 41, backgroundColor: '#14202a', borderWidth: 2, borderColor: '#0e3746', alignItems: 'center', justifyContent: 'center' },
  previewAvatarTxt: { color: '#7fd4ff', fontWeight: '900', fontSize: 26 },
  previewLabel: { color: '#fff', fontWeight: '800', fontSize: 14, marginTop: 14 },

  option: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    backgroundColor: '#101010', borderWidth: 1, borderColor: '#242424', borderRadius: 12,
    padding: 16, marginBottom: 10,
  },
  optionOn: { borderColor: '#FCD000' },
  optionLocked: { opacity: 0.7 },
  optionTitleRow: { flexDirection: 'row', alignItems: 'center' },
  optionTitle: { color: '#fff', fontSize: 15, fontWeight: '800' },
  optionSub: { color: '#aaa', fontSize: 12.5, marginTop: 4, lineHeight: 18 },
  lockHint: { color: '#7fd4ff', fontSize: 12, marginTop: 8, lineHeight: 17 },

  cta: { backgroundColor: '#FCD000', borderRadius: 10, paddingVertical: 12, alignItems: 'center', marginTop: 4 },
  ctaTxt: { color: '#000', fontWeight: '800', fontSize: 14 },
  adminNote: { color: '#888', fontSize: 11.5, marginTop: 12, textAlign: 'center' },
});
